import queryString from "query-string";
import React, { Fragment, ReactNode } from "react";

import { attemptMethod, hasWindow } from "./general";

type ConditionalWrapperProps = {
    condition: boolean;
    wrapper: (children: any) => ReactNode;
    alternative?: (children: any) => ReactNode;
    children?: React.ReactNode;
};

export const ConditionalWrapper = ({
    condition,
    wrapper,
    children,
    alternative,
}: ConditionalWrapperProps) => {
    return (
        <Fragment>
            {condition
                ? wrapper(children)
                : alternative
                ? alternative(children)
                : children}
        </Fragment>
    );
};

export const mapSeries = async (iterable: any[], action: Function) => {
    let res = [];
    for (const x of iterable) {
        res.push(await action(x));
    }
    return res;
};

export const isEmpty = (obj: Object): boolean => {
    return Object.keys(obj).length === 0;
};

export const isFunction = (value: any): boolean => {
    return typeof value === "function";
};

export const cleanUpObjRecursive = (obj: any): any => {
    const tempObj = obj;
    for (const key in tempObj) {
        if (typeof tempObj[key] === "object") {
            if (!Object.keys(tempObj).length) {
                delete tempObj[key];
            } else {
                cleanUpObjRecursive(tempObj[key]);
            }
        } else if (tempObj[key] === undefined) {
            delete tempObj[key];
        }
    }

    return tempObj;
};

export const cleanUpObj = (obj: any): any => {
    Object.keys(obj).forEach(key => obj[key] === undefined && delete obj[key]);
    return obj;
};

export const removeKeysMatching = (
    obj: any,
    predicate: (key: string) => boolean,
) => {
    Object.keys(obj).forEach(key => predicate(key) && delete obj[key]);
    return obj;
};

export const mergeLeft = <TReturn extends object, TToMerge extends object>(
    base: TReturn,
    toMerge: TToMerge,
) =>
    Object.keys(base).reduce(
        (merged, key) => ({
            ...merged,
            [key as keyof typeof base]:
                toMerge[key as keyof typeof toMerge] ??
                base[key as keyof typeof base],
        }),
        {} as TReturn,
    );

export const isDefined = <T,>(value: T | null | undefined): value is T =>
    value !== undefined && value !== null;

export const partitionAndFind = <T,>(
    collection: T[],
    predicate: (element: T) => boolean,
) =>
    collection.reduce(
        ([matchedValue, rest], value) => {
            if (isDefined(matchedValue))
                return [matchedValue, [...rest, value]] as const;

            return predicate(value)
                ? ([value, rest] as const)
                : ([undefined, [...rest, value]] as const);
        },
        [undefined, []] as readonly [undefined | T, readonly T[]],
    );

export const toChunks = <T,>(arr: Array<T>, chunkSize: number) => {
    return arr.reduce((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / chunkSize);

        if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = []; // start a new chunk
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
    }, [] as Array<Array<T>>);
};

export const noop = () => {};

export const isAuthorEnvironment = () => {
    return hasWindow() && !!window?.Granite?.author;
};

export const getAlgoliaQuery = () => {
    return (
        attemptMethod(
            () => queryString.parse(window.location.search)["q"] as string,
            "",
        ) || ""
    );
};
